import { user } from '@tencent/ppd-base-libs';
import { genSharecode, genShareShorturl } from '@tencent/wphk-act-base-cgi';
import { setWxShareInfo } from '@tencent/wphk-act-base-framework';
import { url } from '@tencent/wphk-base-utils';
import { REDIRECT_URL, ShareAdtag } from '../config';
import { C2CConfigShareInfo, WxShareInfo } from '../types';

const C2C_SHARE_CODE = 'c2c_share_code';

function getShareCodeCacheKey(actId: string) {
  const uin = user.getUin();
  return `${uin}_${C2C_SHARE_CODE}_${actId}`;
}

/** 获取缓存微信分享链接 */
export function getShareCodeCache(actId: string) {
  return localStorage.getItem(getShareCodeCacheKey(actId)) || '';
}

/** 设置缓存微信分享链接 */
export function setShareCodeCache(actId: string, shareCode: string) {
  localStorage.setItem(getShareCodeCacheKey(actId), shareCode);
}

/** 清楚缓存微信分享链接 */
export function clearShareCodeCache(actId: string) {
  localStorage.removeItem(getShareCodeCacheKey(actId));
}

/** 生成分享码 */
export async function getShareCode(actId: string): Promise<string> {
  const data = await genSharecode({ actId });
  return data.code;
}

/** 当前页面地址 */
function getPageUrl() {
  return window.location.origin + window.location.pathname;
}

/** 生成短链接 */
export async function buildShortUrl(params: { adtag: string; c2cShareCode: string; actId: string }): Promise<string> {
  const { adtag, c2cShareCode, actId } = params;
  const schemeUrl = url.addUrlParams(getPageUrl(), { adtag, c2cShareCode, version: 1 });
  const originUrl = url.addUrlParams(REDIRECT_URL, { adtag, c2cShareCode, confId: params.actId });
  // 获取短链接
  const data = await genShareShorturl({
    actId,
    schemeUrl,
    originUrl,
  });
  return data.jumpUrl;
}

/** 获取分享码，生成短链接 */
export async function getWxShareInfo(actId: string): Promise<WxShareInfo> {
  // 获取分享码
  const c2cShareCode = await getShareCode(actId);
  // 生成复制内容的短链接
  const shortUrlForCopy = await buildShortUrl({
    adtag: ShareAdtag.copy,
    c2cShareCode,
    actId,
  });
  // 生成微信分享链接（H5/小程序）
  const wxShareUrl = url.addUrlParams(getPageUrl(), {
    adtag: ShareAdtag.wx,
    c2cShareCode,
  });
  // 这里设置微信分享链接到本地，拉起裂变弹窗的时候，直接先进行微信分享设置，这样可以减少因为没有点击分享按钮/复制分享链接导致的分享问题
  // 实现目的：
  // 首次访问活动 -> 拉起裂变弹窗 -> 点击分享按钮/复制分享链接 -> 点击右上角进行分享
  // 二次访问活动 -> 拉起裂变弹窗 -> 直接右上角进行分享
  setShareCodeCache(actId, wxShareUrl);
  return {
    c2cShareCode,
    shortUrlForCopy,
    wxShareUrl,
  };
}

/** 设置 H5 和小程序微信分享 */
export function handleSetWxShareInfo({ link, shareInfo }: { link: string; shareInfo: C2CConfigShareInfo }) {
  setWxShareInfo({
    mp: {
      title: shareInfo.title,
      link,
      imgUrl: shareInfo.minProLogo,
    },
    wx: {
      title: shareInfo.title,
      link,
      imgUrl: shareInfo.wxLogo,
      desc: shareInfo.desc,
    },
  });
}
